import React from "react";
import { Card } from "./ui/card";
import { Home, Brain, FileCheck, Handshake, ArrowRight } from "lucide-react";
import { processSteps } from "../data/mockData";

const iconMap = {
  Home,
  Brain,
  FileCheck,
  Handshake
};

const ProcessSection = () => {
  return (
    <section id="how-it-works" className="relative py-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1615404420216-cc423164563f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/75"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 to-green-900/30"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            How Our AI Technology Works
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Our proprietary algorithm instantly analyzes your property using real-time data 
            from multiple platforms to generate fair, competitive cash offers.
          </p>
        </div>

        {/* Enhanced Process Steps */}
        <div className="relative">
          {/* Connection Line for Desktop */}
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400/50 via-green-400/50 to-blue-400/50"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {processSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              
              return (
                <div key={step.step} className="text-center relative">
                  {/* Step Number & Icon */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl backdrop-blur-sm">
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-4 border-blue-600 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-white">{step.title}</h3>
                    <p className="text-gray-200 leading-relaxed">{step.description}</p>
                  </div>

                  {/* Arrow for desktop */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-16 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-blue-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Enhanced Timeline Benefits */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-blue-600">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-sm">5min</span>
            </div>
            <h4 className="font-bold text-white mb-2">Instant Analysis</h4>
            <p className="text-gray-200">Our AI analyzes millions of data points from our proprietary HOT BUYER algorithm</p>
          </Card>

          <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-green-600">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-sm">2-4</span>
            </div>
            <h4 className="font-bold text-white mb-2">Week Closing</h4>
            <p className="text-gray-200">Close on your property in just 2-4 weeks, not months like traditional sales.</p>
          </Card>

          <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-blue-600">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-sm">0%</span>
            </div>
            <h4 className="font-bold text-white mb-2">Commission Fees</h4>
            <p className="text-gray-200">No realtor commissions, no closing costs, no surprise fees. What we offer is what you get.</p>
          </Card>
        </div>

        {/* Enhanced CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600/90 to-green-600/90 backdrop-blur-sm rounded-2xl p-8 text-white border border-white/20">
            <h3 className="text-2xl font-bold mb-4">Ready to Experience AI-Powered Selling?</h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Join thousands of homeowners who've discovered the faster, smarter way to sell their property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const offerSection = document.getElementById('offer-submission');
                  if (offerSection) {
                    offerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Start My Sale Now
              </button>
              <button 
                onClick={() => window.location.href = 'tel:1-888-334-3524'}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-all"
              >
                Call 1-888-334-3524
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;